import React, { useState, useEffect } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Row, Col, FormGroup, Label } from "reactstrap";
import QwikForm from "components/form/QwikForm";
import QwikInput from "components/form/QwikInput";
import QwikSelect from "components/form/QwikSelect";
import Select from 'react-select';
import { useUpdateUserMutation } from "store/features/users/userManagementApi";
import { toast } from "sonner";

const EditUserModal = ({ isOpen, toggle, user, onUserUpdate }) => {
  const [updateUser, { isLoading }] = useUpdateUserMutation();
  const [profileImage, setProfileImage] = useState(null);
  const [crmPermissions, setCrmPermissions] = useState([]);
  const [formData, setFormData] = useState({});

  // Define available CRM permissions
  const crmPermissionOptions = [
    { value: "qwik_it", label: "Qwik IT" },
    { value: "qwik_medic", label: "Qwik Medic" },
    { value: "qwik_bistro", label: "Qwik Bistro" },
  ];

  // Initialize form data and CRM permissions when user data changes
  useEffect(() => {
    if (user) {
      // Parse CRM permissions from user data
      const parsedPermissions = parseCrmPermissions(user.crm_permissions);
      setCrmPermissions(parsedPermissions);

      // Set other form data
      setFormData({
        nickname: user.nickname || '',
        personal_email: user.personal_email || '',
        phone: user.phone || '',
        role: user.role || '',
        position: user.position || '',
        gender: user.gender || '',
        account_status: user.account_status || '',
        address: user.address || '',
      });
    }
  }, [user]);

  const parseCrmPermissions = (crmData) => {
    if (!crmData) return [];

    try {
      // If it's already an array
      if (Array.isArray(crmData)) {
        return crmData;
      }

      // If it's a string, try to parse
      if (typeof crmData === 'string') {
        // Try JSON parse first
        try {
          const parsed = JSON.parse(crmData);
          if (Array.isArray(parsed)) {
            return parsed;
          }
        } catch {
          // If not JSON, try comma-separated
          return crmData.split(',').map(item => item.trim()).filter(item => item);
        }
      }
    } catch (error) {
      console.error('Error parsing CRM permissions:', error);
    }

    return [];
  };

  const handleCrmPermissionsChange = (selectedOptions) => {
    const values = selectedOptions ? selectedOptions.map(option => option.value) : [];
    setCrmPermissions(values);
  };

  const handleSubmit = async (data) => {
    try {
      const submitFormData = new FormData();

      // Append user data
      submitFormData.append('role', data.role || formData.role);
      submitFormData.append('nickname', data.nickname || formData.nickname);
      submitFormData.append('personal_email', data.personal_email || formData.personal_email);
      submitFormData.append('phone', data.phone || formData.phone || '');
      submitFormData.append('gender', data.gender || formData.gender || '');
      submitFormData.append('account_status', data.account_status || formData.account_status || '');
      submitFormData.append('address', data.address || formData.address || '');
      submitFormData.append('position', data.position || formData.position || '');

      // Append CRM permissions as JSON string
      submitFormData.append('crm_permissions', JSON.stringify(crmPermissions));

      // Append profile image if selected
      if (profileImage) {
        submitFormData.append('image', profileImage);
      }

      await updateUser({ id: user.id, formData: submitFormData }).unwrap();
      toast.success("User updated successfully");
      onUserUpdate();
      handleModalClose();
    } catch (error) {
      toast.error("Failed to update user");
      console.error('User update error:', error);
    }
  };

  const handleImageChange = (event) => {
    const file = event.target.files[0];
    setProfileImage(file);
  };

  const handleModalClose = () => {
    setProfileImage(null);
    setCrmPermissions([]);
    toggle();
  };

  const getSelectedCrmOptions = () => {
    return crmPermissionOptions.filter(option =>
      crmPermissions.includes(option.value)
    );
  };

  return (
    <Modal isOpen={isOpen} toggle={handleModalClose} size="lg">
      <ModalHeader toggle={handleModalClose}>Edit User - {user?.nickname}</ModalHeader>
      <QwikForm onSubmit={handleSubmit} defaultValues={user}>
        <ModalBody>
          <Row>
            <Col lg={6}>
              <div className="mb-3">
                <QwikInput
                  name="nickname"
                  label="Name"
                  placeholder="Enter full name"
                  type="text"
                  className="form-control"
                  required
                />
              </div>
            </Col>
            <Col lg={6}>
              <div className="mb-3">
                <QwikInput
                  name="personal_email"
                  label="Email"
                  placeholder="Enter email"
                  type="email"
                  className="form-control"
                  required
                />
              </div>
            </Col>
            <Col lg={6}>
              <div className="mb-3">
                <QwikInput
                  name="phone"
                  label="Phone Number"
                  placeholder="Enter phone number"
                  type="text"
                  className="form-control"
                />
              </div>
            </Col>
            <Col lg={6}>
              <div className="mb-3">
                <QwikSelect
                  name="role"
                  label="Role"
                  options={[
                    { value: "admin", label: "Admin" },
                    { value: "project_manager", label: "Project Manager" },
                    { value: "developer", label: "Developer" },
                    { value: "client", label: "Client" },
                    { value: "marketing", label: "Marketing" },
                    { value: "designer", label: "Designer" },
                    { value: "hr", label: "HR" }
                  ]}
                  required
                />
              </div>
            </Col>

            <Col lg={6}>
              <div className="mb-3">
                <QwikInput
                  name="position"
                  label="Position"
                  placeholder="Enter position"
                  type="text"
                  className="form-control"
                />
              </div>
            </Col>

            <Col lg={6}>
              <div className="mb-3">
                <QwikSelect
                  name="gender"
                  label="Gender"
                  options={[
                    { value: "male", label: "Male" },
                    { value: "female", label: "Female" },
                    { value: "other", label: "Other" }
                  ]}
                />
              </div>
            </Col>

            <Col lg={6}>
              <div className="mb-3">
                <QwikSelect
                  name="account_status"
                  label="Account Status"
                  options={[
                    { value: true, label: "Active" },
                    { value: false, label: "Disabled" }
                  ]}
                />
              </div>
            </Col>

            <Col lg={6}>
              <div className="mb-3">
                <QwikInput
                  name="address"
                  label="Address"
                  placeholder="Enter full address"
                  type="textarea"
                  className="form-control"
                  rows="3"
                />
              </div>
            </Col>

            {/* CRM Permissions Multi-Select */}
            <Col lg={6}>
              <div className="mb-3">
                <Label className="form-label">CRM Permissions</Label>
                <Select
                  isMulti
                  name="crm_permissions"
                  options={crmPermissionOptions}
                  value={getSelectedCrmOptions()}
                  onChange={handleCrmPermissionsChange}
                  className="basic-multi-select"
                  classNamePrefix="select"
                  placeholder="Select CRM permissions..."
                  closeMenuOnSelect={false}
                />
              </div>
            </Col>

            {/* Profile Image Upload */}
            <Col lg={6}>
              <FormGroup>
                <Label for="profile_image" className="form-label">
                  Profile Image
                </Label>
                {user?.image && (
                  <div className="mb-2">
                    <img
                      src={user.image}
                      alt={user.nickname}
                      className="rounded"
                      style={{ width: "40px", height: "40px", objectFit: "cover" }}
                    />
                  </div>
                )}
                <div className="custom-file">
                  <input
                    type="file"
                    className="custom-file-input"
                    id="profile_image"
                    accept=".jpg,.jpeg,.png,.gif"
                    onChange={handleImageChange}
                  />
                </div>
              </FormGroup>
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter>
          <Button type="button" color="light" onClick={handleModalClose}>
            Cancel
          </Button>
          <Button type="submit" color="primary" disabled={isLoading}>
            {isLoading ? "Updating..." : "Update User"}
          </Button>
        </ModalFooter>
      </QwikForm>
    </Modal>
  );
};

export default EditUserModal;